import React, { useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { useDay } from '@datepicker-react/hooks';
import clsx from 'clsx';
import isBetween from 'dayjs/plugin/isBetween';
import { useGlobal } from '@rcg/ui-react';
import DatepickerContext from '@rcg/ui-react/lib/components/DatepickerResponsive/datepickerContext';
import { compareDays } from '@rcg/ui-react/lib/utils/dates';
import useStyles from './index.style';

const Day = (props) => {
  const classes = useStyles();
  const {
    data: { localizedDayjs },
  } = useGlobal();

  const { day, dayType, date, singleDaySelection = false } = props;
  const dayRef = useRef(null);
  localizedDayjs.extend(isBetween);

  const {
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateSelect,
    onDateFocus,
    onDateHover,
    start,
    end,
    incomingStartDate,
    incomingEndDate,
  } = useContext(DatepickerContext);
  const { isSelected, disabledDate, onClick, onKeyDown, onMouseEnter, tabIndex } = useDay({
    date,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateFocus,
    onDateSelect,
    onDateHover,
    dayRef,
  });

  const getDayType = () => {
    if (compareDays(start, date)) {
      return 'start';
    }
    if (compareDays(end, date)) {
      return 'end';
    }
    if (disabledDate) {
      return 'disabled';
    }
    if (isSelected) {
      return 'inRange';
    }
    return 'enabled';
  };

  const dayTypeVal = dayType || getDayType();
  const shouldHaveUnderscore = localizedDayjs(date).isBetween(
    localizedDayjs(incomingStartDate),
    localizedDayjs(incomingEndDate),
    null,
    '[]',
  );
  const isBetweenIncomingDates = localizedDayjs(date).isBetween(
    localizedDayjs(incomingStartDate).add(1, 'days'),
    localizedDayjs(incomingEndDate).subtract(1, 'days'),
    null,
    '[]',
  );

  const getEvent = (event) => {
    if (isBetweenIncomingDates) {
      return () => {};
    }
    return event;
  };

  return (
    <div
      className={clsx(
        classes.dayWrapper,
        singleDaySelection ? '' : `wrapper-type-${dayTypeVal}`,
        (isBetweenIncomingDates || disabledDate) && 'wrapper-type-not-selectable',
      )}>
      <button
        data-testid={`test-id-${dayTypeVal}`}
        onClick={getEvent(onClick)}
        onKeyDown={getEvent(onKeyDown)}
        onMouseEnter={getEvent(onMouseEnter)}
        tabIndex={tabIndex}
        type="button"
        ref={dayRef}
        className={clsx(
          classes.dayButton,
          `button-type-${dayTypeVal}`,
          (isBetweenIncomingDates || disabledDate) && 'button-type-not-selectable',
        )}>
        {day} {shouldHaveUnderscore ? <div className={classes.underscore} /> : <></>}
      </button>
    </div>
  );
};

Day.propTypes = {
  day: PropTypes.string,
  dayType: PropTypes.string,
  date: PropTypes.instanceOf(Date),
};

Day.defaultProps = {
  day: '',
  dayType: '',
  date: new Date(),
};

export default Day;
